import React from 'react'
import styled from 'styled-components'

import {
  EditConturedExactIcon,
  ShareIcon,
  WheelFilledExactIcon,
} from '@root/shared/ui-kit'

import { ProfileHeaderConnections } from './profile-header-connections'
import { ProfileRanks } from './profile-ranks'

type Props = {
  avatar: React.ReactNode
  title: string
  mainSubtitle?: React.ReactNode
  mainFooter?: React.ReactNode
  onAvatarClick?: () => void
  connections?: {
    followers: number | string
    following: number | string
    onFollowersClick: () => void
    onFollowingsClick: () => void
  }
  counts?: {
    coins: number | string
    likes: number | string
    mentions: number | string
    onCoinsClick: () => void
    onLikesClick: () => void
    onMentionsClick: () => void
  }
  actions: ReadonlyArray<ProfileHeaderAction>
}

export interface ProfileHeaderAction {
  onClick: () => void
  icon: 'wheel' | 'pen' | 'share'
}

const Icon = {
  pen: <EditConturedExactIcon width={12} height={12} />,
  wheel: <WheelFilledExactIcon width={12} height={12} />,
  share: <ShareIcon width={12} height={12} />,
}

export const ProfileHeader: React.FC<Props> = (props) => {
  return (
    <Root>
      <Top>
        <Main>
          <MainBody>
            {
              <Actions>
                {props.actions.map((action, index) => (
                  <EditButton
                    key={index}
                    type="button"
                    onClick={action.onClick}
                  >
                    {Icon[action.icon]}
                  </EditButton>
                ))}
              </Actions>
            }
            <MainAvatar onClick={props.onAvatarClick}>
              {props.avatar}
            </MainAvatar>
            <MainContent>
              <MainTitle>{props.title}</MainTitle>
              {props.mainSubtitle ? (
                <MainSubtitle>{props.mainSubtitle}</MainSubtitle>
              ) : undefined}
            </MainContent>
          </MainBody>
          {props.mainFooter ? (
            <MainFooter>{props.mainFooter}</MainFooter>
          ) : undefined}
        </Main>
        {props.connections ? (
          <ProfileHeaderConnections {...props.connections} />
        ) : undefined}
      </Top>
      {props.counts ? (
        <Bottom>
          <ProfileRanks {...props.counts} />
        </Bottom>
      ) : undefined}
    </Root>
  )
}

const Root = styled.div``
const Bottom = styled.div`
  border-bottom: 1px solid var(--backgroundA-3);
`

const Top = styled.div`
  position: relative;
  border-radius: 12px;
  background: var(--backgroundA-2);
`

const Main = styled.div`
  padding: 18px;
  border-radius: 12px;

  /* fallback */
  background:
    radial-gradient(
      circle at 60% -40%,
      var(--primary-9),
      var(--primary-9) 20%,
      var(--background-1) 65%
    ),
    var(--background-1);

  display: grid;
  border: 1px solid var(--background-3);
  gap: 10px;
`

const MainBody = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 20px;
`
const MainFooter = styled.div`
  margin: 0 -10px -10px;
`

const MainAvatar = styled.div`
  max-width: 70px;
  max-height: 70px;

  cursor: pointer;
`
const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  justify-content: center;
`
const MainTitle = styled.div`
  font-weight: 600;
  font-size: 16px;
  color: var(--text-12);
  word-break: break-word;
  margin-right: 14px;
  text-wrap: balance;
`
const MainSubtitle = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`
const Actions = styled.div`
  position: absolute;
  top: 4px;
  right: 4px;
  display: flex;
  gap: 4px;
`
const EditButton = styled.button`
  all: unset;
  cursor: pointer;
  display: flex;
  padding: 8px;
  border-radius: 8px;
  width: 28px;
  height: 28px;
  background: var(--backgroundA-2);
  box-sizing: border-box;
  transition: all 0.3s ease;

  &:hover,
  &:focus {
    background: var(--backgroundA-4);
  }
`
